import { useInfiniteQuery } from '@tanstack/react-query'
import { Empty, Flex, Select, Spin } from 'antd'
import { SelectProps } from 'antd/lib'
import { useState } from 'react'

import { useCounterpartServiceGetOtherInstitutionEndpointKey } from 'src/api/queries'
import {
  CounterpartService,
  GetInstitutionListResponse,
  GetOtherInstitutionListResponseItem,
  LegalEntityStatus
} from 'src/api/requests'
import { DEFAULT_PER_PAGE } from 'src/app-constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  apiStatus?: LegalEntityStatus
} & SelectProps

export const OtherInstitutionsSelect = ({ apiStatus, ...props }: Props) => {
  const { t } = useLocales()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)

  const { data, fetchNextPage, isFetching } = useInfiniteQuery({
    queryKey: [
      useCounterpartServiceGetOtherInstitutionEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await CounterpartService.getOtherInstitutionListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        undefined,
        apiStatus
      )

      return {
        data: response?.data ?? [],
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data: GetInstitutionListResponse
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const options =
    data?.pages.flatMap(
      (page) =>
        page.data?.map((item: GetOtherInstitutionListResponseItem) => ({
          value: item.id,
          label: item?.name
        })) || []
    ) ?? []

  return (
    <Select
      allowClear
      popupMatchSelectWidth={true}
      filterOption={false}
      options={options}
      className="full-width"
      onSearch={(value) => setSearch(value)}
      onClear={() => setSearch('')}
      loading={isFetching}
      placeholder={t('menu.other_institutions')}
      showSearch={true}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        if (target.scrollTop + target.offsetHeight === target.scrollHeight)
          fetchNextPage()
      }}
      notFoundContent={
        props.loading ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty />
        )
      }
      {...props}
    />
  )
}
